import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import {
  PageContainer,
  PageHero,
  Section,
  Protected,
} from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

const sections = [
  { title: 'Information collection & usage', body: "We collect different types of information for various purposes from multiple sources intending to provide and improve our services online. Our products or services may contain links to, or be embedded within, other companies' websites and services with their own privacy policies." },
  { title: 'Types of data collected', items: [
    ['Personal', 'While using our service, we may ask for personally identifiable information used to contact or identify you - e.g., email, mobile number, first name and last name.'],
    ['Use of data', 'ESAY uses collected data to provide and maintain the service, notify about changes, enable participation in interactive features, provide customer care, analyse usage, and detect / prevent technical issues.'],
  ] },
  { title: 'What information do we collect?', items: [
    ['Product & service activations', 'Device and application type, plus unique device/application/network/subscription identifiers.'],
    ['Use of products & services', 'Web servers automatically create records of your visit: IP address, access times, linking sites, pages visited, features used, content viewed, and browser/application type.'],
    ['Information you provide', 'When you create an account, make a purchase, request services, or participate in research, we may ask for your name, email, phone, address, user names, feedback, device information, age, gender, language, and payment information.'],
    ['Your transactions with us', 'Records of purchases, downloads, content provided, requests, agreements, products/services, payment & delivery details, and customer-care communications.'],
    ['Information from partners', 'Information from industry partners and publicly available sources such as websites of institutions, research organisations, and business registries.'],
  ] },
  { title: 'Why do we process personal data?', items: [
    ['Providing products & services', 'To perform the contract between you and ESAY, ensure functionality and security, identify you, and prevent/investigate fraud.'],
    ['Accounts', 'Account creation requires basic contact details (name, email, country, date of birth). You may voluntarily provide more (photo, preferences).'],
    ['Developing & managing', 'To develop and manage products, customer care, sales, and marketing.'],
    ['Communicating with you', 'To inform you of service changes, critical alerts, and customer-care notices.'],
    ['Marketing & recommendations', 'To inform you about new products/promotions and conduct market research where permitted or with consent.'],
  ] },
  { title: 'Legal basis for processing', items: [
    ['Performance of a contract', 'To perform obligations under the Service Terms applicable to the product or service you are using.'],
    ['Your consent', 'Where you consent. You may revoke consent at any time; it will not impact processing carried out prior to withdrawal.'],
    ['Legal obligations', 'To comply with laws and regulations.'],
    ['Legitimate interests', 'To manage, develop, secure, and in limited cases market our products and services.'],
  ] },
  { title: 'How long do we retain personal data?', body: 'We only collect personal data necessary for the purposes for which it is collected, and retain it no longer than necessary. Account data is maintained while the account exists; activity logs are typically retained only for a short period.' },
  { title: 'Your consent and social sharing', body: 'We may share your personal data if we have your consent. Some services allow sharing with other users - consider carefully before disclosing anything accessible to other users.' },
  { title: 'Mandatory disclosures', body: 'We may be obligated by law to disclose personal data to authorities or third parties (e.g. law enforcement). We may also disclose data to defend ESAY\'s legitimate interests in legal proceedings or governmental requests.' },
  { title: 'Data quality & security', body: 'We take reasonable steps to keep personal data accurate and to delete incorrect or unnecessary data. We use commercially acceptable means to protect personal data, but no internet transmission or electronic storage is 100% secure.' },
  { title: 'Disclosure of data', body: 'ESAY may disclose your personal data in good faith to comply with legal obligations, defend ESAY\'s rights or property, prevent or investigate wrongdoing, protect user / public safety, and defend against legal liability.' },
  { title: 'Changes to this privacy policy', body: "ESAY may change this Policy or modify/withdraw access to this site. If the Policy changes in a material way, we will post a notice at the top for 30 days. Please re-visit periodically." },
];

function PrivacyBlock({ idx, title, body, items }) {
  return (
    <article className="
      relative overflow-hidden rounded-2xl
      border border-border-subtle bg-surface-paper
      p-5 sm:p-6 mb-3
    ">
      <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber/30 to-transparent" />
      <div className="mb-1 inline-flex items-center gap-1.5">
        <span aria-hidden className="inline-flex h-1 w-1 rounded-full bg-amber" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Section {String(idx + 1).padStart(2, '0')}</span>
      </div>
      <h2 className="text-[16px] sm:text-[18px] font-bold tracking-[-0.018em] text-fg-primary">{title}</h2>
      <div aria-hidden className="mt-2 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />
      {body && <p className="mt-3 text-[13px] leading-[1.7] text-fg-secondary">{body}</p>}
      {items && (
        <ul className="mt-2">
          {items.map(([label, text], j) => (
            <li key={j} className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-3 py-2.5 border-b border-border-subtle last:border-b-0">
              <div className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-amber leading-[1.4]">{label}</div>
              <div className="text-[13px] leading-[1.65] text-fg-secondary">{text}</div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export const Privacy = () => {
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    Axios.post('/new_apis/getsinglemetadata', { type: 'privacy' }, { headers: { key } }).then((res) => {
      if (res.data.status) setMetadata(res.data.metadata[0]);
      else setMetadata({ seo_title: 'Privacy Policy | ESAY' });
    });
  }, []);

  return (
    <Protected onCopy={(e) => e.preventDefault()}>
      {metadata && (
        <Helmet>
          <title>{metadata.seo_title}</title>
          {metadata.seo_desc && <meta name="description" content={metadata.seo_desc} />}
          {metadata.seo_keywords && <meta name="keywords" content={metadata.seo_keywords} />}
        </Helmet>
      )}

      <PageContainer maxWidth="md">
        <PageHero
          compact
          kicker="Legal"
          title="Privacy Policy"
          subtitle="To ensure vital information is available in one location, ESAY was formed. Your privacy matters to us."
        />

        <Section>
          <article className="
            relative overflow-hidden rounded-2xl
            border border-amber/24 bg-amber/5
            p-5 mb-3
            flex gap-3
          ">
            <span aria-hidden className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-amber" />
            <p className="text-[13px] leading-[1.7] text-fg-secondary">
              By using the service, you agree to the collection and use of information in accordance with this policy. <strong className="font-bold text-amber">ESAY and its affiliates</strong> are committed to respecting your privacy and complying with applicable data protection and privacy laws.
            </p>
          </article>

          {sections.map((s, i) => (
            <PrivacyBlock key={i} idx={i} title={s.title} body={s.body} items={s.items} />
          ))}

          <div className="mt-2 rounded-2xl border border-border-subtle bg-surface-paper px-5 py-4 text-center">
            <p className="text-[13px] text-fg-secondary">
              Contact us at{' '}
              <a href="mailto:contact@esay.in" className="font-bold text-amber hover:text-amber-light underline-offset-2 hover:underline">contact@esay.in</a>
              {' '}or{' '}
              <a href="mailto:contact@arawinz.com" className="font-bold text-amber hover:text-amber-light underline-offset-2 hover:underline">contact@arawinz.com</a>.
            </p>
          </div>
        </Section>
      </PageContainer>
    </Protected>
  );
};
